import { useState } from 'react';
import { Ban, RotateCcw, Search, ShieldCheck, ShoppingCart, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Skeleton,
  StatCard,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import { statusTone } from '@/lib/status';
import {
  useCustomerAction,
  useCustomerDetail,
  useCustomerQueue,
  useQueueSummary,
  type CustomerStatus,
} from '@/lib/customers-api';
import { cn, errorMessage, formatCurrency, formatDateTime } from '@/lib/utils';

/**
 * The customer register (spec §29).
 *
 * Distinct from `/customers/verification`, which is a *queue* — a worklist of
 * people waiting on a decision. This is the register: everyone, in any state,
 * searchable, and the place to suspend or reinstate an account or switch
 * ordering on.
 *
 * The three gates are shown as three separate badges rather than one status,
 * because they are three separate facts (§5) and collapsing them here would
 * teach the reader a model the API does not hold.
 */

const STATUS_TABS: Array<{ label: string; value: CustomerStatus[] | null }> = [
  { label: 'All', value: null },
  { label: 'Verified', value: ['VERIFIED'] },
  { label: 'In review', value: ['UNDER_REVIEW', 'VERIFICATION_PENDING'] },
  { label: 'Incomplete', value: ['REGISTERED', 'PROFILE_INCOMPLETE'] },
  { label: 'Rejected', value: ['REJECTED'] },
  { label: 'Suspended', value: ['SUSPENDED', 'BLOCKED'] },
];

export function CustomersPage() {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const activeTab = STATUS_TABS[tab] ?? STATUS_TABS[0]!;

  const customers = useCustomerQueue({
    page,
    limit: 20,
    search,
    status: activeTab.value ?? undefined,
  });
  const summary = useQueueSummary();

  const byStatus = summary.data?.byStatus ?? {};

  return (
    <div>
      <PageHeader
        title="Customers"
        description="Every registered business, and the three gates that decide what they can do."
        action={
          <Link
            to="/customers/verification"
            className="inline-flex h-9 items-center rounded-lg border border-line px-3 text-sm font-medium text-ink-muted hover:text-brand-600"
          >
            Verification queue
          </Link>
        }
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summary.isLoading ? (
          [0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-24" />)
        ) : (
          <>
            <StatCard
              label="Verified buyers"
              value={byStatus.VERIFIED ?? 0}
              icon={<ShieldCheck className="size-5" aria-hidden="true" />}
              tone="success"
            />
            <StatCard
              label="Awaiting review"
              value={summary.data?.awaitingReview ?? 0}
              icon={<Users className="size-5" aria-hidden="true" />}
              tone="warning"
            />
            <StatCard
              label="Missing documents"
              value={summary.data?.pendingDocuments ?? 0}
              icon={<Users className="size-5" aria-hidden="true" />}
              tone="warning"
            />
            <StatCard
              label="Suspended or blocked"
              value={(byStatus.SUSPENDED ?? 0) + (byStatus.BLOCKED ?? 0)}
              icon={<Ban className="size-5" aria-hidden="true" />}
              tone="danger"
            />
          </>
        )}
      </div>

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 flex-1">
            <Search
              className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search company, customer code or GST number"
              className="pl-9"
              aria-label="Search customers"
            />
          </div>

          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Customer status">
            {STATUS_TABS.map((entry, index) => (
              <button
                key={entry.label}
                type="button"
                role="tab"
                aria-selected={tab === index}
                onClick={() => {
                  setTab(index);
                  setPage(1);
                }}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  tab === index
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-muted hover:bg-canvas hover:text-ink',
                )}
              >
                {entry.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        {customers.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : customers.isError ? (
          <div className="p-4">
            <ErrorAlert title="Could not load customers">
              {errorMessage(customers.error)}
            </ErrorAlert>
          </div>
        ) : (customers.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No customers match"
            description="Try a different status or clear the search."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Business</Th>
                <Th>Contact</Th>
                <Th>Status</Th>
                <Th>Gates</Th>
                <Th>Registered</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {customers.data?.items.map((customer) => (
                <tr key={customer.id} className="hover:bg-canvas/60">
                  <Td>
                    <p className="font-medium text-ink">{customer.companyName}</p>
                    <p className="text-xs text-ink-subtle">
                      {customer.customerCode}
                      {customer.gstNumber && ` · ${customer.gstNumber}`}
                    </p>
                  </Td>
                  <Td>
                    <p className="text-sm text-ink">{customer.contactName ?? '—'}</p>
                    <p className="text-xs text-ink-subtle">{customer.contactEmail}</p>
                  </Td>
                  <Td>
                    <Badge tone={statusTone(customer.status)}>
                      {customer.status.replace(/_/g, ' ').toLowerCase()}
                    </Badge>
                  </Td>
                  <Td>
                    {/* Three separate facts, shown separately (§5). */}
                    <div className="flex flex-wrap gap-1">
                      <Badge tone={customer.isVerified ? 'success' : 'neutral'}>KYC</Badge>
                      <Badge
                        tone={
                          customer.securityDepositStatus === 'PAID' ||
                          customer.securityDepositStatus === 'NOT_REQUIRED' ||
                          customer.securityDepositStatus === 'HELD'
                            ? 'success'
                            : 'neutral'
                        }
                      >
                        Deposit
                      </Badge>
                      <Badge tone={customer.canOrder ? 'success' : 'neutral'}>Order</Badge>
                    </div>
                  </Td>
                  <Td className="text-xs text-ink-subtle">
                    {formatDateTime(customer.createdAt)}
                  </Td>
                  <Td className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setSelectedId(customer.id)}>
                      Manage
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {customers.data && customers.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
            <p className="text-sm text-ink-subtle">
              {customers.data.pagination.total} customers · page{' '}
              {customers.data.pagination.page} of {customers.data.pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!customers.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!customers.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <CustomerDrawer customerId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

/* ==========================================================================
   Detail drawer
   ========================================================================== */

type PendingAction = 'suspend' | 'reinstate' | 'ordering';

function CustomerDrawer({
  customerId,
  onClose,
}: {
  customerId: string | null;
  onClose: () => void;
}) {
  const detail = useCustomerDetail(customerId);
  const mutation = useCustomerAction(customerId);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState('');

  const customer = detail.data;

  const run = (action: string, body?: Record<string, unknown>) => {
    mutation.mutate(
      { action, method: action === 'ordering' ? 'PATCH' : 'POST', body },
      {
        onSuccess: () => {
          setPending(null);
          setReason('');
        },
      },
    );
  };

  return (
    <Drawer
      open={Boolean(customerId)}
      onClose={() => {
        setPending(null);
        onClose();
      }}
      title={customer?.companyName ?? 'Customer'}
    >
      {detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      ) : detail.isError ? (
        <ErrorAlert title="Could not load this customer">{errorMessage(detail.error)}</ErrorAlert>
      ) : (
        customer && (
          <div className="space-y-5">
            <section>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge tone={statusTone(customer.status)}>
                  {customer.status.replace(/_/g, ' ').toLowerCase()}
                </Badge>
                <Badge tone={customer.isVerified ? 'success' : 'neutral'}>
                  {customer.isVerified ? 'KYC verified' : 'Not verified'}
                </Badge>
                <Badge tone={customer.canOrder ? 'success' : 'neutral'}>
                  {customer.canOrder ? 'Ordering on' : 'Ordering off'}
                </Badge>
              </div>

              <p className="text-sm text-ink">{customer.customerCode}</p>
              {customer.contact && (
                <p className="mt-1 text-sm text-ink-muted">
                  {customer.contact.firstName} {customer.contact.lastName} ·{' '}
                  <a href={`mailto:${customer.contact.email}`} className="text-brand-600">
                    {customer.contact.email}
                  </a>
                </p>
              )}
              {customer.gstNumber && (
                <p className="mt-1 text-xs text-ink-subtle">GSTIN: {customer.gstNumber}</p>
              )}
            </section>

            {customer.securityDeposit && (
              <section className="rounded-lg border border-line p-4">
                <h3 className="mb-2 text-sm font-semibold text-ink">Security deposit</h3>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  {(
                    [
                      ['Required', customer.securityDeposit.requiredAmount],
                      ['Paid', customer.securityDeposit.paidAmount],
                      ['Outstanding', customer.securityDeposit.outstandingAmount],
                      ['Balance', customer.securityDeposit.balanceAmount],
                    ] as Array<[string, number]>
                  ).map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-xs text-ink-subtle">{label}</dt>
                      <dd className="font-medium tabular-nums text-ink">
                        {formatCurrency(value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {mutation.isError && (
              <ErrorAlert title="Could not do that">{errorMessage(mutation.error)}</ErrorAlert>
            )}

            {pending ? (
              <section className="space-y-3 rounded-lg border border-line p-4">
                <h3 className="text-sm font-semibold text-ink">
                  {pending === 'suspend'
                    ? 'Suspend this account'
                    : pending === 'reinstate'
                      ? 'Reinstate this account'
                      : 'Change ordering'}
                </h3>

                <Field
                  label="Reason"
                  htmlFor="reason"
                  required={pending === 'suspend'}
                  hint="Recorded on the customer's verification history."
                >
                  <Textarea
                    id="reason"
                    rows={3}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </Field>

                <div className="flex gap-2">
                  <Button
                    disabled={mutation.isPending || (pending === 'suspend' && !reason.trim())}
                    onClick={() =>
                      run(
                        pending === 'ordering' ? 'ordering' : pending,
                        pending === 'ordering'
                          ? { canOrder: !customer.canOrder, reason: reason || undefined }
                          : { reason },
                      )
                    }
                  >
                    {mutation.isPending ? 'Saving…' : 'Confirm'}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={mutation.isPending}
                    onClick={() => {
                      setPending(null);
                      setReason('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </section>
            ) : (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink">Actions</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    disabled={!customer.isVerified}
                    onClick={() => setPending('ordering')}
                    title={
                      customer.isVerified
                        ? undefined
                        : 'Ordering can only be switched on for a verified business.'
                    }
                  >
                    <ShoppingCart className="size-4" aria-hidden="true" />
                    {customer.canOrder ? 'Switch off ordering' : 'Switch on ordering'}
                  </Button>

                  {customer.status === 'SUSPENDED' || customer.status === 'BLOCKED' ? (
                    <Button variant="outline" onClick={() => setPending('reinstate')}>
                      <RotateCcw className="size-4" aria-hidden="true" />
                      Reinstate
                    </Button>
                  ) : (
                    <Button variant="danger" onClick={() => setPending('suspend')}>
                      <Ban className="size-4" aria-hidden="true" />
                      Suspend
                    </Button>
                  )}

                  <Link
                    to="/customers/verification"
                    className="col-span-2 inline-flex h-10 items-center justify-center rounded-lg border border-line text-sm font-medium text-ink-muted hover:text-brand-600"
                  >
                    Review documents in the verification queue
                  </Link>
                </div>
                <p className="mt-2 text-xs text-ink-subtle">
                  Verification decisions are made in the queue, where the documents are.
                </p>
              </section>
            )}
          </div>
        )
      )}
    </Drawer>
  );
}
